import http from '@/lib/http'
import { useMutation, useQuery } from '@tanstack/react-query'

/** API response shape for a single investigation detail. */
export type APIInvestigationDetail = {
  orderItemHash: string
  orderItemName: string | null
  orderItemStatus: string | null
  productHash: string | null
  productVariationHash: string | null
  productName: string | null
  orderHash: string | null
  orderNumber: string | null
  createdAt: string | null
  updatedAt: string | null
  patientHash: string | null
  patientName: string | null
  allergies: string | null
  conditions: string | null
  medications: string | null
  familyHistory: string | null
  externalId: string | null
  resultsRaw: string | null
  notes: string | null
  csNotes: string | null
}

export const fetchInvestigationByOrderItemHash = async (orderItemHash: string): Promise<APIInvestigationDetail> => {
  return (await http.get(`/investigations/${orderItemHash}`)) as APIInvestigationDetail
}

export const useInvestigationDetailQuery = (orderItemHash: string | undefined) => {
  return useQuery({
    queryKey: ['investigation-detail', orderItemHash],
    queryFn: () => fetchInvestigationByOrderItemHash(orderItemHash!),
    enabled: !!orderItemHash
  })
}

/**
 * Update an investigation's order-item status.
 * @param orderItemHash - Order item hash (used as the investigation identifier)
 * @param status - New status in `wc-item-*` format (e.g. `wc-item-investigation-pending`)
 */
export const updateInvestigationStatus = async (orderItemHash: string, status: string): Promise<void> => {
  await http.patch(`/investigations/${orderItemHash}/status`, { status })
}

/**
 * Mutation hook for updating investigation status.
 */
export const useUpdateInvestigationStatusMutation = () => {
  return useMutation({
    mutationFn: ({ orderItemHash, status }: { orderItemHash: string; status: string }) =>
      updateInvestigationStatus(orderItemHash, status)
  })
}
